import React, { useMemo, useState } from "react";
import { FaCalendarAlt, FaLayerGroup, FaUsers, FaClipboardList, FaDiscord, FaMedkit, FaUserShield, FaTable, FaUserClock } from "react-icons/fa";
import { useTripContext } from "./TripContext";
import TripDetailsActions from "./TripDetailsActions";
import TripDetailsInfo from "./TripDetailsInfo";
import PhotoDetails from "./TripDetailsPhoto";
import TripVariantsSection from "./TripVariantsSection";
import TripRegistrations from "./Registrations/TripRegistrations";
import RegistrationDetails from "./Registrations/RegistrationDetails";
import ParticipantDetails from "./Registrations/ParticipantDetails";
import ParticipantsListWrapper from "./Participants/ParticipantsListWrapper";
import AccessPanel from "./Access/AccessPanel";
import Insurances from "./Insurances/Insurances";
import Summary from "./Summary/Summary";
import DiscordChannelsSection from "./Discord/DiscordChannelsSection";
import DiscordChannelDetails from "./Discord/DiscordChannelDetails";
import { useToast } from "../../Contexts/ToastContext";
import useApi from "../CustomHooks/useApi";
import { tripAPI } from "../../api/apiClient";
import ConfirmWithPhrasePopup from "../Common/ConfirmWithPhrasePopup";
import { getActiveRegistrations, getTotalParticipants } from "../../utils/registrationMetrics";

const TripDetailsContent = ({ onEditClick, onVariantClick, onRegistrationsClick, onAttendanceClick, onAccessClick, onInsuranceClick, onDiscordClick, onPriorityClick, onSummaryClick, tripGuid, activeSection = "registrations", sectionContent = null, mobileRoot = false }) => {
  const { updateTripStatus, tripData, fetchTripData, hasExplicitAccess } = useTripContext();
  const { showToast, clearToast } = useToast();
  const { callApi: changeStatus, isLoading: isChangingStatus } = useApi();
  const { callApi: changeSettlementStatus, isLoading: isChangingSettlement } = useApi();
  const [showConfirmReady, setShowConfirmReady] = useState(false);

  const handleStatusChange = async (newStatus, onSuccess) => {
    showToast("Zmiana statusu...", "processing");
    await changeStatus(() => tripAPI.changeTripStatus(tripGuid, newStatus), (newStatus) => {
      updateTripStatus(newStatus);
      onSuccess();
      showToast("Status wycieczki został zaktualizowany!", "success");
    }, clearToast);
  };

  const handleSettlementChange = async (newStatus) => {
    showToast("Aktualizacja statusu rozliczenia...", "processing");
    await changeSettlementStatus(async () => tripAPI.changeSettlementStatus(tripGuid, newStatus), async () => {
      await fetchTripData();
      showToast("Status rozliczenia został zaktualizowany", "success");
    }, clearToast);
  };

  const dashboard = useMemo(() => {
    const variants = tripData?.variants || [];
    const registrations = tripData?.registrations || [];
    const dates = variants.flatMap(v => [v.startDate, v.endDate]).filter(Boolean).map(d => new Date(d)).filter(d => !Number.isNaN(d.getTime()));
    const start = dates.length ? new Date(Math.min(...dates)) : null;
    const end = dates.length ? new Date(Math.max(...dates)) : null;
    const activeRegistrations = getActiveRegistrations(registrations);
    const waitlistRegistrations = registrations.filter(r => r.status === 2);
    const participants = getTotalParticipants(activeRegistrations);
    const waitlistParticipants = getTotalParticipants(waitlistRegistrations);
    return { variants, registrations, start, end, activeRegistrations, participants, waitlistParticipants };
  }, [tripData]);

  const formatDate = (date) => date ? new Date(date).toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" }) : "Brak terminu";

  const renderSection = () => {
    if (sectionContent === "registrationDetails") return <RegistrationDetails inline />;
    if (sectionContent === "participantDetails") return <ParticipantDetails registrations={dashboard.registrations} />;
    if (sectionContent === "discordDetails") return <DiscordChannelDetails />;
    switch (activeSection) {
      case "participants": return <ParticipantsListWrapper />;
      case "discord": return <DiscordChannelsSection />;
      case "insurances": return <Insurances />;
      case "access": return <AccessPanel />;
      case "summary": return <Summary />;
      case "registrations":
      default: return <TripRegistrations registrations={dashboard.registrations} variants={dashboard.variants} />;
    }
  };

  if (!tripData) return <div>Brak danych wycieczki.</div>;

  return (
    <>
      <div className="trip-overview-desktop">
        <div className="trip-overview">
          <section className="trip-overview-main td-card">
            <div className="trip-overview-hero">
              <div className="trip-overview-photo-wrap">
                {tripData.photoLink ? <img className="trip-overview-photo" src={tripData.photoLink} alt={tripData.name} /> : <div className="trip-overview-photo-placeholder"><i className="fa-regular fa-image" /></div>}
              </div>
              <div className="trip-overview-copy">
                <div className="trip-overview-title-row">
                  <h2>{tripData.name}</h2>
                  <TripDetailsActions mode="header" onEditClick={onEditClick} onPriorityClick={onPriorityClick} tripGuid={tripGuid} tripSettlementStatus={tripData.tripSettlementStatus} hasExplicitAccess={hasExplicitAccess} onMarkReadyForSettlement={() => setShowConfirmReady(true)} />
                </div>
                <div className="trip-overview-meta">
                  <span><FaCalendarAlt /> {formatDate(dashboard.start)} – {formatDate(dashboard.end)}</span>
                  <span className="trip-overview-type">{tripData.isInternalTrip ? "Wyjazd specjalny" : "Wyjazd otwarty"}</span>
                </div>
                <div className="trip-overview-chips">
                  <span><FaLayerGroup /> {dashboard.variants.length} {dashboard.variants.length === 1 ? "wariant" : "warianty"}</span>
                  <span><FaUsers /> {dashboard.participants} uczestników</span>
                  <span><FaUserClock /> {dashboard.waitlistParticipants} na rezerwie</span>
                </div>
                <div className="trip-overview-status-row">
                  <div className="trip-overview-inline-status">
                    <TripDetailsInfo status={tripData.status} isInternalTrip={tripData.isInternalTrip} isCurrentUserMentor={tripData.isCurrentUserMentor} onConfirmStatusChange={handleStatusChange} isLoading={isChangingStatus} tripSettlementStatus={tripData.tripSettlementStatus} takenForSettlementByUser={tripData.takenForSettlementByUser} />
                  </div>
 
                </div>
                <div className="trip-hero-variants">
                  <span>Warianty wycieczki</span>
                  <div>
                    {dashboard.variants.map(variant => (
                      <div key={variant.guid} className="trip-hero-variant" role="button" tabIndex={0} onClick={() => onVariantClick(variant.guid)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onVariantClick(variant.guid); }}>
                        <span className="trip-hero-variant-icon"><i className="fa-solid fa-route" /></span>
                        <span className="trip-hero-variant-copy"><strong>{variant.name}</strong><small>{formatDate(variant.startDate)} – {formatDate(variant.endDate)}</small></span>
                        <i className="fa-solid fa-chevron-right" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <nav className="trip-overview-tabs" aria-label="Sekcje wycieczki">
              <button className={`trip-overview-tab ${activeSection === "registrations" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onRegistrationsClick}><FaClipboardList /> Rejestracje</button>
              <button className={`trip-overview-tab ${activeSection === "participants" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onAttendanceClick}><FaUsers /> Uczestnix</button>
              <button className={`trip-overview-tab ${activeSection === "discord" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onDiscordClick}><FaDiscord /> Discord</button>
              {hasExplicitAccess && <button className={`trip-overview-tab ${activeSection === "insurances" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onInsuranceClick}><FaMedkit /> Ubezpieczenia</button>}
              {hasExplicitAccess && <button className={`trip-overview-tab ${activeSection === "access" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onAccessClick}><FaUserShield /> Dostęp</button>}
              <button className={`trip-overview-tab ${activeSection === "summary" ? "trip-overview-tab--active" : ""}`} type="button" onClick={onSummaryClick}><FaTable /> Zestawienia</button>
            </nav>
            <div className="trip-overview-content">{renderSection()}</div>
          </section>
        </div>
      </div>

      {mobileRoot ? (
        <div className="trip-overview-mobile">
          <TripDetailsActions mode="mobile" onEditClick={onEditClick} onRegistrationsClick={onRegistrationsClick} onAttendanceClick={onAttendanceClick} onAccessClick={onAccessClick} onInsuranceClick={onInsuranceClick} onPriorityClick={onPriorityClick} onDiscordClick={onDiscordClick} onSummaryClick={onSummaryClick} onMarkReadyForSettlement={() => setShowConfirmReady(true)} tripGuid={tripGuid} tripSettlementStatus={tripData.tripSettlementStatus} hasExplicitAccess={hasExplicitAccess} />
          <PhotoDetails photoUrl={tripData.photoLink} />
          <TripDetailsInfo status={tripData.status} isInternalTrip={tripData.isInternalTrip} isCurrentUserMentor={tripData.isCurrentUserMentor} onConfirmStatusChange={handleStatusChange} isLoading={isChangingStatus} tripSettlementStatus={tripData.tripSettlementStatus} takenForSettlementByUser={tripData.takenForSettlementByUser} />
          <TripVariantsSection variants={tripData.variants} onVariantClick={onVariantClick} />
        </div>
      ) : <div className="trip-section-mobile-only">{renderSection()}</div>}

      {showConfirmReady && <ConfirmWithPhrasePopup phrase="rozlicz" text="Czy na pewno chcesz zgłosić wycieczkę do rozliczenia?" onConfirm={() => { setShowConfirmReady(false); handleSettlementChange(2); }} onCancel={() => setShowConfirmReady(false)} />}
    </>
  );
};

export default TripDetailsContent;
